import { useEffect, useMemo } from 'react';
import { InfiniteData, infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { AnyActivity, Evidence } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import {
  GetOracleActivitiesParams,
  GetOracleActivitiesResponse,
  ORACLE_EVENTS_PAGE_SIZE
} from '@blisspointmedia/bpm-types/dist/OracleApi';
import { reportError } from '@src/utils/errorReporting';
import { OracleLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import { EVENTS_QUERY_KEY } from './activity-feed-constants';
import { useOracleCidScope } from './useOracleCidScope';

// Stops paging when Oracle hands back a cursor already used in this feed. TanStack calls this on
// every render, so it must stay pure; the hook reports the stall separately.
export const nextPageCursor = (
  last: { nextCursor: string | null },
  allPageParams: unknown[]
): string | undefined => {
  if (!last.nextCursor || allPageParams.includes(last.nextCursor)) return undefined;
  return last.nextCursor;
};

// The cursor that stalled paging (the last page's cursor, already used), or null.
export const findStalledCursor = (
  data: InfiniteData<GetOracleActivitiesResponse> | undefined
): string | null => {
  const nextCursor = data?.pages[data.pages.length - 1]?.nextCursor;
  return nextCursor && data.pageParams.includes(nextCursor) ? nextCursor : null;
};

export const memberIds = (activity: AnyActivity): string[] =>
  activity.foldedIds?.length ? activity.foldedIds : [activity.id];

// Evidence ids are only unique within one item, so prefix the repeat's to keep React keys unique.
const evidenceWithUniqueIds = (activity: AnyActivity): Evidence[] =>
  activity.evidence.map(evidence => ({ ...evidence, id: `${activity.id}-${evidence.id}` }));

const mergeRepeatedGroup = (first: AnyActivity, repeat: AnyActivity): AnyActivity => ({
  ...first,
  foldedIds: memberIds(first).concat(memberIds(repeat)),
  // A repeat carries only the group's remaining events, so the two counts never overlap.
  commentCount: first.commentCount + repeat.commentCount,
  evidence: first.evidence.concat(evidenceWithUniqueIds(repeat))
});

// A folded walk re-serves a group that straddles a page boundary under a new id, carrying the
// group's remaining events, so a repeat is merged into the first face rather than dropped.
export const flattenActivityPages = (
  data: InfiniteData<GetOracleActivitiesResponse> | undefined
): AnyActivity[] => {
  const activities: AnyActivity[] = [];
  const indexByGroupKey = new Map<string, number>();
  const pageActivities = data?.pages.flatMap(page => page.events) ?? [];
  for (const activity of pageActivities) {
    const groupKey = activity.foldedGroupKey;
    if (groupKey === undefined) {
      activities.push(activity);
    } else {
      const firstIndex = indexByGroupKey.get(groupKey);
      if (firstIndex === undefined) {
        indexByGroupKey.set(groupKey, activities.length);
        activities.push(activity);
      } else {
        activities[firstIndex] = mergeRepeatedGroup(activities[firstIndex], activity);
      }
    }
  }
  return activities;
};

export interface OracleEventsDataResult {
  activities: AnyActivity[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: ReturnType<typeof useInfiniteQuery<GetOracleActivitiesResponse>>['fetchNextPage'];
  error: Error | null;
  requiresCidSelection: boolean;
}

// Shared by the feed and its counts so both observe one cache entry and one request per page.
export const oracleFeedQueryOptions = ({
  cid,
  selectedCids,
  params: scopeParams
}: ReturnType<typeof useOracleCidScope>) =>
  infiniteQueryOptions({
    queryKey: [...EVENTS_QUERY_KEY, cid, selectedCids],
    queryFn: async ({ pageParam }) => {
      const params: GetOracleActivitiesParams = { limit: ORACLE_EVENTS_PAGE_SIZE, ...scopeParams };
      if (typeof pageParam === 'string') {
        params.cursor = pageParam;
      }
      const res = await OracleLambdaFetch('/events', { params });
      return awaitJSON<GetOracleActivitiesResponse>(res);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last, _allPages, _lastPageParam, allPageParams) =>
      nextPageCursor(last, allPageParams)
  });

export const useOracleEventsData = (): OracleEventsDataResult => {
  const query = useInfiniteQuery(oracleFeedQueryOptions(useOracleCidScope()));

  const activities = useMemo(() => flattenActivityPages(query.data), [query.data]);

  // Keyed on the stalled cursor (paging stops once it stalls), so each stall reports once.
  const stalledCursor = findStalledCursor(query.data);
  const pagesLoaded = query.data?.pages.length ?? 0;
  useEffect(() => {
    if (!stalledCursor) return;
    reportError(new Error('Oracle events cursor did not advance'), {
      operation: 'oracleEventsPaging',
      extra: { pagesLoaded }
    });
  }, [stalledCursor, pagesLoaded]);
  const requiresCidSelection = Boolean(query.data?.pages[0]?.requiresCidSelection);

  return {
    activities,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error ?? null,
    requiresCidSelection
  };
};
